import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import apiClient from 'src/helpers/apiClient';
import { getFormattedErr } from 'src/helpers';
import type {
  ScheduleInterviewBody,
  RescheduleInterviewBody,
} from 'src/openapi';

export const INTERVIEWS_PAGE_SIZE = 15;

export const fetchOrgInterviews = createAsyncThunk(
  'interview/fetchOrgInterviews',
  async (
    {
      orgId,
      page = 1,
      pageSize = INTERVIEWS_PAGE_SIZE,
      status,
      jobId,
      candidateUserId,
      upcoming,
    }: {
      orgId: string;
      page?: number;
      pageSize?: number;
      status?: string;
      jobId?: string;
      candidateUserId?: string;
      upcoming?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetOrgInterviews({
        orgId,
        candidateUserId,
        jobId,
        page,
        pageSize,
        status,
        upcoming,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// The candidate's current active interview with this org (if any), used to switch
// the "Schedule interview" entry point into a "view scheduled interview" state.
export const fetchCandidateActiveInterview = createAsyncThunk(
  'interview/candidateActive',
  async (
    { orgId, candidateUserId }: { orgId: string; candidateUserId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetOrgInterviews({
        orgId,
        candidateUserId,
        page: 1,
        pageSize: 20,
      });
      const list = response.data.data ?? [];
      const ACTIVE = ['Scheduled', 'Accepted', 'Rescheduled'];
      const now = Date.now();
      const active = list
        .filter((iv) => {
          if (!ACTIVE.includes(iv.status ?? '')) return false;
          // A still-"Scheduled" row whose window has passed is history, not a
          // live booking — callers use this to decide whether to offer a fresh
          // schedule or the reschedule/cancel view.
          if (!iv.scheduled_start_at) return false;
          const endsAt =
            new Date(iv.scheduled_start_at).getTime() +
            (iv.duration_minutes ?? 60) * 60000;
          return endsAt > now;
        })
        .sort((a, b) => {
          const ta = a.scheduled_start_at
            ? new Date(a.scheduled_start_at).getTime()
            : 0;
          const tb = b.scheduled_start_at
            ? new Date(b.scheduled_start_at).getTime()
            : 0;
          return ta - tb;
        });
      return active[0] ?? null;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Count of all upcoming (active, not-yet-passed) interviews — sidebar badge.
const ACTIVE_STATUSES = ['Scheduled', 'Accepted', 'Rescheduled'];
const countUpcoming = (
  list: { status?: string; scheduled_start_at?: string | null }[],
) => {
  const now = Date.now();
  return list.filter(
    (iv) =>
      ACTIVE_STATUSES.includes(iv.status ?? '') &&
      !!iv.scheduled_start_at &&
      new Date(iv.scheduled_start_at).getTime() >= now,
  ).length;
};

export const fetchInterviewTodayCount = createAsyncThunk(
  'interview/todayCount',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetOrgInterviews({ orgId, page: 1, pageSize: 100 });
      return countUpcoming(response.data.data ?? []);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchMyInterviews = createAsyncThunk(
  'interview/fetchMyInterviews',
  async (
    {
      page = 1,
      pageSize = INTERVIEWS_PAGE_SIZE,
      upcoming,
    }: { page?: number; pageSize?: number; upcoming?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetMyInterviews({ page, pageSize, upcoming });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Count of the candidate's own upcoming (active) interviews — applied-jobs badge.
export const fetchMyInterviewTodayCount = createAsyncThunk(
  'interview/myTodayCount',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetMyInterviews({ page: 1, pageSize: 100 });
      return countUpcoming(response.data.data ?? []);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const scheduleInterview = createAsyncThunk(
  'interview/schedule',
  async (
    { orgId, body }: { orgId: string; body: ScheduleInterviewBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiScheduleInterview({ orgId, scheduleInterviewBody: body });
      enqueueSnackbar('Interview scheduled', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      if (errorKey !== 'insufficient_employer_credits') {
        enqueueSnackbar(getFormattedErr(errorKey), { variant: 'error' });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const rescheduleInterview = createAsyncThunk(
  'interview/reschedule',
  async (
    {
      orgId,
      interviewId,
      body,
    }: { orgId: string; interviewId: string; body: RescheduleInterviewBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiRescheduleInterview({
        interviewId,
        orgId,
        rescheduleInterviewBody: body,
      });
      enqueueSnackbar('Interview rescheduled', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const cancelInterview = createAsyncThunk(
  'interview/cancel',
  async (
    {
      orgId,
      interviewId,
      cancelReason,
    }: { orgId: string; interviewId: string; cancelReason?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiCancelInterview({
        interviewId,
        orgId,
        cancelInterviewBody: { cancel_reason: cancelReason },
      });
      enqueueSnackbar('Interview cancelled', { variant: 'info' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const respondInterview = createAsyncThunk(
  'interview/respond',
  async (
    {
      interviewId,
      response: decision,
    }: { interviewId: string; response: 'Accepted' | 'Declined' },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiRespondInterview({
        interviewId,
        respondInterviewBody: { response: decision },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getInterview = createAsyncThunk(
  'interview/get',
  async (
    { interviewId, shareToken }: { interviewId: string; shareToken?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetInterview({ interviewId, displayName: shareToken });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const joinInterview = createAsyncThunk(
  'interview/join',
  async (
    {
      interviewId,
      shareToken,
      consent,
    }: {
      interviewId: string;
      shareToken?: string;
      consent?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      )
        // display_name is unused — guests are logged in, so the roster uses their
        // account name. Pass undefined rather than threading a dead param.
        .interviewApiJoinInterview({ interviewId, consent, st: shareToken });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Best-effort attendance beacon: records the caller's leave time. Fire-and-forget
// from the leave handler — failure is silent (the cleanup cron finalizes any row
// left open).
export const leaveInterview = createAsyncThunk(
  'interview/leave',
  async (
    { interviewId, shareToken }: { interviewId: string; shareToken?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiLeaveInterview({ interviewId, st: shareToken });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Host admits a waiting guest from the waiting-room tray. Returns the refreshed
// interview detail (updated roster).
export const admitParticipant = createAsyncThunk(
  'interview/admitParticipant',
  async (
    {
      orgId,
      interviewId,
      participantId,
    }: { orgId: string; interviewId: string; participantId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiAdmitParticipant({
        interviewId,
        orgId,
        pid: participantId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Host removes a participant from the room; they cannot rejoin.
export const removeParticipant = createAsyncThunk(
  'interview/removeParticipant',
  async (
    {
      orgId,
      interviewId,
      participantId,
    }: { orgId: string; interviewId: string; participantId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiRemoveParticipant({
        interviewId,
        orgId,
        pid: participantId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Employer toggle for the shareable-link / multi-participant setting on an interview.
export const updateInterviewSettings = createAsyncThunk(
  'interview/updateSettings',
  async (
    {
      orgId,
      interviewId,
      allowMultipleParticipants,
      maxParticipants,
      requireHostAdmit,
    }: {
      orgId: string;
      interviewId: string;
      allowMultipleParticipants?: boolean;
      maxParticipants?: number | null;
      requireHostAdmit?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiUpdateInterviewSettings({
        interviewId,
        orgId,
        updateInterviewSettingsBody: {
          ...(allowMultipleParticipants !== undefined && {
            allow_multiple_participants: allowMultipleParticipants,
          }),
          ...(maxParticipants !== undefined && {
            max_participants: maxParticipants,
          }),
          ...(requireHostAdmit !== undefined && {
            require_host_admit: requireHostAdmit,
          }),
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchRecording = createAsyncThunk(
  'interview/fetchRecording',
  async (
    { orgId, interviewId }: { orgId: string; interviewId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetInterviewRecording({ interviewId, orgId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// --- Admin: recordings + transcripts across all orgs ---

export const fetchAdminRecordings = createAsyncThunk(
  'interview/fetchAdminRecordings',
  async (
    params: {
      page?: number;
      pageSize?: number;
      meetingType?: string;
      recordingStatus?: string;
      transcriptStatus?: string;
      includeAll?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetAdminInterviewRecordings(params);
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchAdminTranscript = createAsyncThunk(
  'interview/fetchAdminTranscript',
  async ({ interviewId }: { interviewId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiGetAdminInterviewTranscript({ interviewId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
        variant: 'error',
      });
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const unlockRecording = createAsyncThunk(
  'interview/unlockRecording',
  async (
    { orgId, interviewId }: { orgId: string; interviewId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getInterviewApi()
      ).interviewApiUnlockInterviewRecording({ interviewId, orgId });
      enqueueSnackbar('Recording unlocked', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      if (errorKey !== 'insufficient_employer_credits') {
        enqueueSnackbar(getFormattedErr(errorKey), { variant: 'error' });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);
